import { memo, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { spacing, typography, colors, borderRadius, elevation, size } from '@izettle/otto';
import { useOnResize } from '../../../hooks/use-on-resize.js';
import { useStateRef } from '../../../utils/state-ref.js';
import { ZINDEX_SINGLE_SELECT_POP_OVER, ZINDEX_SINGLE_SELECT_POP_OVER_FULLSCREEN } from '../helpers/constants.js';
import { jsx } from 'react/jsx-runtime';

const mixinIsExpanded = css`
  ${_ref => {
  let {
    isExpanded
  } = _ref;
  return isExpanded ? `
      opacity: 1;
      visibility: visible;
    ` : `
      opacity: 0;
      transform: translateY(${spacing.xxSmall});
      pointer-events: none;
      visibility: hidden;
    `;
}}
`;
const PopOverPlacement = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "popover__PopOverPlacement",
  componentId: "zettle__sc-serqsh-0"
})(["position:relative;width:100%;z-index:", ";"], ZINDEX_SINGLE_SELECT_POP_OVER));

/* Here z-index is set to 20, to be above the FloatingLabels of adjacent components */
const PopOverContainer = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "popover__PopOverContainer",
  componentId: "zettle__sc-serqsh-1"
})(["", " ", " margin:0;width:fit-content;overflow-y:scroll;background:", ";cursor:default;display:flex;flex-direction:column;padding:", ";border-radius:", ";box-shadow:", ";transition:opacity 0.2s,transform 0.2s;min-height:", ";position:absolute;", " ", " ", ""], _ref2 => {
  let {
    left
  } = _ref2;
  return `
      left: ${left}px;
    `;
}, typography.regular, colors.background.default, spacing.medium, borderRadius.large, elevation.level3, size.large, mixinIsExpanded, _ref3 => {
  let {
    isLoading
  } = _ref3;
  return isLoading && `
      cursor: progress;
    `;
}, _ref4 => {
  let {
    hasActions
  } = _ref4;
  return hasActions && `
      padding-bottom: 0;
    `;
}));
const PopOverMobile = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "popover__PopOverMobile",
  componentId: "zettle__sc-serqsh-2"
})(["position:fixed;margin:0;top:0;bottom:0;left:0;right:0;background:", ";transition:opacity 0.2s,transform 0.2s;display:flex;flex-direction:column;padding:", " 0 0 0;z-index:", ";align-self:flex-end;", ""], colors.background.default, spacing.xSmall, ZINDEX_SINGLE_SELECT_POP_OVER_FULLSCREEN, mixinIsExpanded));
const PopOver = _ref5 => {
  let {
    children,
    isExpanded,
    isMobile,
    onRefList
  } = _ref5;
  const [refListPlacement, setRefListPlacement] = useStateRef(node => node);
  const [refListContainer, setRefListContainer] = useStateRef(node => node);
  const [shift, setShift] = useState(0);
  const handleShift = useCallback(() => {
    const bbox = refListContainer?.getBoundingClientRect() ?? null;
    const left = bbox?.left ?? 0;
    const width = bbox && bbox.right - bbox.left || 0;
    const screenWidth = window.innerWidth;
    const overflow = screenWidth - left - width;
    const offset = overflow < 0 ? overflow : 0;
    setShift(offset);
  }, [refListContainer]);
  useEffect(() => {
    if (isMobile) {
      onRefList && refListPlacement && onRefList(refListPlacement);
    } else {
      onRefList && refListContainer && onRefList(refListContainer);
    }
  }, [isMobile, refListPlacement, refListContainer]);
  useOnResize(() => {
    if (!isMobile) {
      handleShift();
    }
  }, [isMobile, refListContainer]);
  useEffect(() => {
    if (!isMobile) {
      handleShift();
    }
  }, [isMobile, refListContainer]);
  return isMobile ? ( /*#__PURE__*/createPortal(
  
  // eslint-disable-next-line jsx-a11y/role-supports-aria-props
  jsx(PopOverMobile, {
    isExpanded: isExpanded,
    ref: setRefListPlacement,
    children: children
  }), document.body)) : /*#__PURE__*/jsx(PopOverPlacement, {
    ref: setRefListPlacement,
    children: /*#__PURE__*/jsx(PopOverContainer, {
      left: shift,
      ref: setRefListContainer,
      isExpanded: isExpanded,
      children: children
    })
  });
};

export { PopOver };
//# sourceMappingURL=popover.js.map
